import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

export default function MetodoContratacaoAtiva() {
  const beneficios = [
    { icon: 'ri-eye-line', texto: 'Entender exatamente o que está contratando' },
    { icon: 'ri-bar-chart-grouped-line', texto: 'Comparar cotações de forma técnica' },
    { icon: 'ri-price-tag-3-line', texto: 'Negociar comissão e reduzir custos' },
    { icon: 'ri-shield-check-line', texto: 'Evitar armadilhas contratuais' },
    { icon: 'ri-thumb-up-line', texto: 'Contratar com mais segurança' },
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: imgRef, isVisible: imgVisible } = useScrollAnimation();
  const { ref: listRef, visibleItems } = useStaggerAnimation(beneficios.length);

  return (
    <section
      className="relative bg-teal-50/40 py-12 sm:py-16 lg:py-28 overflow-hidden"
      aria-labelledby="metodo-contratacao-heading"
    >
      <div className="container mx-auto px-5 sm:px-6 lg:px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center min-h-[500px] lg:min-h-[620px]">

          {/* ── Coluna de texto ── */}
          <div
            ref={headerRef as React.RefObject<HTMLDivElement>}
            className={`flex flex-col justify-center lg:pr-8 transition-all duration-700 ease-out ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-300 px-3 sm:px-4 py-2 rounded-full mb-5 sm:mb-7 w-fit">
              <i className="ri-book-2-line text-teal-600 text-sm" aria-hidden="true" />
              <span className="text-teal-700 font-bold text-xs uppercase tracking-widest">Método de Contratação Ativa</span>
            </div>

            {/* Título */}
            <h2
              id="metodo-contratacao-heading"
              className="text-2xl sm:text-3xl lg:text-4xl xl:text-[2.65rem] font-bold text-petrol-900 mb-5 sm:mb-6 leading-[1.2]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Aprenda a renovar seu seguro{' '}
              <span className="text-teal-600">pagando menos e com mais proteção</span>
            </h2>

            {/* Texto introdutório */}
            <p className="text-sm sm:text-base lg:text-lg text-petrol-700 leading-relaxed mb-2 sm:mb-3">
              Um método estruturado que coloca você no controle da negociação — sem precisar trocar de corretor.
            </p>
            <p className="text-xs sm:text-sm lg:text-base text-petrol-600 leading-relaxed mb-5 sm:mb-6">
              Com o método certo, você passa a:
            </p>

            {/* Lista de benefícios */}
            <ul
              ref={listRef as React.RefObject<HTMLUListElement>}
              className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8"
              aria-label="Benefícios do método de contratação ativa"
            >
              {beneficios.map((beneficio, i) => (
                <li
                  key={i}
                  className={`flex items-center gap-2.5 sm:gap-3 bg-teal-50 border border-teal-100 rounded-lg sm:rounded-xl px-3 sm:px-4 py-3 sm:py-3.5 transition-all duration-500 ease-out ${
                    visibleItems[i] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                  }`}
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-teal-100 rounded-lg shrink-0">
                    <i className={`${beneficio.icon} text-teal-600 text-sm sm:text-base`} aria-hidden="true" />
                  </div>
                  <span className="text-xs sm:text-sm lg:text-base text-petrol-900 font-semibold">{beneficio.texto}</span>
                </li>
              ))}
            </ul>

            {/* Destaque — barra escura */}
            <div className="bg-petrol-900 rounded-lg sm:rounded-xl px-4 sm:px-6 py-3.5 sm:py-4 mb-6 sm:mb-8 flex items-start gap-2.5 sm:gap-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-lightbulb-line text-teal-400 text-base sm:text-lg" aria-hidden="true" />
              </div>
              <p className="text-xs sm:text-sm lg:text-base text-gray-200 leading-relaxed">
                Você deixa de ser um <strong className="text-white">cliente passivo</strong> e passa a ter{' '}
                <strong className="text-white">controle total da decisão</strong>.
              </p>
            </div>

            {/* Bloco institucional IBX — premium */}
            <div className="relative bg-gradient-to-br from-strategic-green-50 via-white to-petrol-50 border border-strategic-green-200 rounded-xl sm:rounded-2xl p-5 sm:p-7 mb-5 sm:mb-7 shadow-md">
              <div className="absolute top-0 left-0 w-1 h-full bg-strategic-green-500 rounded-l-xl sm:rounded-l-2xl" aria-hidden="true" />
              <div className="flex items-center gap-2 mb-3 sm:mb-4 pl-2">
                <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-strategic-green-100 rounded-lg">
                  <i className="ri-building-2-line text-strategic-green-600 text-sm sm:text-base" aria-hidden="true" />
                </div>
                <span className="text-strategic-green-700 font-extrabold text-xs uppercase tracking-widest">IBX Brasil</span>
              </div>
              <p className="text-xs sm:text-sm lg:text-base text-petrol-800 leading-relaxed pl-2 mb-3">
                Na prática, o método transforma a forma como você contrata seguro, gerando:
              </p>
              <ul className="pl-2 space-y-1.5">
                {['economia real', 'mais proteção', 'decisões seguras'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-checkbox-circle-fill text-strategic-green-500 text-sm" aria-hidden="true" />
                    </div>
                    <span className="text-xs sm:text-sm text-petrol-800 font-semibold">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Destaque final — fundo escuro com citação */}
            <div className="bg-petrol-900 rounded-lg sm:rounded-xl px-4 sm:px-6 py-4 sm:py-5 flex items-start sm:items-center gap-3 sm:gap-4 mb-6 sm:mb-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-action-orange-500/20 rounded-lg shrink-0">
                <i className="ri-double-quotes-l text-action-orange-400 text-lg sm:text-xl" aria-hidden="true" />
              </div>
              <p className="text-sm sm:text-base lg:text-lg font-semibold text-white italic leading-snug">
                "Seguro barato sem método é prejuízo."
              </p>
            </div>

            {/* CTA */}
            <div className="mt-6 sm:mt-8">
              <a
                href="#analise-gratuita"
                className="flex sm:inline-flex items-center justify-center gap-2 sm:gap-3 bg-action-orange-500 hover:bg-action-orange-600 active:scale-95 text-white font-bold text-sm sm:text-base lg:text-lg px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 w-full sm:w-auto"
                aria-label="Acessar o método de contratação ativa"
              >
                <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center shrink-0">
                  <i className="ri-book-open-line text-white text-base sm:text-lg" aria-hidden="true" />
                </div>
                <span className="whitespace-nowrap">Quero acessar o método agora</span>
                <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center shrink-0">
                  <i className="ri-arrow-right-line text-white text-sm sm:text-base" aria-hidden="true" />
                </div>
              </a>
              <p className="text-xs text-petrol-500 mt-3 text-center sm:text-left">
                Apenas R$67
              </p>
            </div>
          </div>

          {/* ── Coluna de imagem ── */}
          <div
            ref={imgRef as React.RefObject<HTMLDivElement>}
            className={`relative hidden lg:block h-full min-h-[400px] sm:min-h-[500px] lg:min-h-[620px] mt-4 lg:mt-0 transition-all duration-700 ease-out delay-200 ${
              imgVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative h-full rounded-xl sm:rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=confident%20professional%20businesswoman%20in%20modern%20corporate%20office%20reviewing%20and%20organizing%20insurance%20documents%20with%20a%20calm%20satisfied%20expression%2C%20standing%20at%20a%20bright%20organized%20desk%2C%20business%20attire%2C%20warm%20natural%20lighting%20from%20large%20window%2C%20shallow%20depth%20of%20field%2C%20premium%20editorial%20photography%2C%20clean%20neutral%20tones%2C%20sense%20of%20control%20and%20clarity%2C%20contemporary%20minimalist%20workspace&width=900&height=700&seq=metodo-contratacao-ativa-img-v1&orientation=portrait"
                alt="Profissional analisando documentos com confiança em ambiente corporativo moderno"
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="lazy"
                width="900"
                height="700"
              />

              {/* Overlay gradiente */}
              <div className="absolute inset-0 bg-gradient-to-r from-teal-50/20 via-transparent to-petrol-900/20" aria-hidden="true" />
              <div className="absolute inset-0 bg-gradient-to-t from-petrol-900/55 via-transparent to-transparent" aria-hidden="true" />

              {/* Badge overlay — controle total */}
              <div className="absolute bottom-8 left-8 right-8">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-5 shadow-2xl border border-white/60">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-teal-100 rounded-xl shrink-0">
                      <i className="ri-shield-check-fill text-teal-600 text-xl" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-[10px] text-teal-600 font-extrabold uppercase tracking-widest mb-1">Contratação Ativa</p>
                      <p className="text-sm font-bold text-petrol-900 leading-snug">
                        Você no controle — do início ao fim
                      </p>
                      <p className="text-xs text-petrol-500 mt-1">Sem depender de quem tem interesse na sua desinformação.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Badge superior — estatística */}
              <div className="absolute top-8 right-8">
                <div className="bg-petrol-900/90 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-xl border border-white/10 text-center">
                  <p className="text-3xl font-extrabold text-teal-400 leading-none">R$67</p>
                  <p className="text-[10px] text-gray-300 uppercase tracking-wide mt-1 leading-tight max-w-[90px]">
                    para dominar o método completo
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}